// src/components/operationCenter/widgets/GenericCandlestickWidget.tsx
// OHLCV candlestick chart from any backend endpoint.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Settings } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps, GenericCandlestickConfig } from "@/types/dashboard";
import {
  fetchEndpoint,
  extractRecords,
  parseParams,
  paramsToString,
  formatValue,
} from "./shared/genericWidgetUtils";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import { CHART_COLORS, createTooltip, createGrid } from "@/utils/charts/theme";
import { WIDGET_EMPTY } from "../WidgetWrapper";

function buildCandlestickOption(
  records: Record<string, unknown>[],
  tsField: string,
  openField: string,
  highField: string,
  lowField: string,
  closeField: string,
  volumeField?: string,
): EChartsOption {
  const dates = records.map((r) => {
    const v = r[tsField];
    if (typeof v === "number") {
      const ms = v < 1e10 ? v * 1000 : v;
      return new Date(ms).toISOString().slice(0, 10);
    }
    return String(v);
  });

  // ECharts candlestick format: [open, close, low, high]
  const candleData = records.map((r) => [
    Number(r[openField] ?? 0),
    Number(r[closeField] ?? 0),
    Number(r[lowField] ?? 0),
    Number(r[highField] ?? 0),
  ]);

  const volumeData = volumeField
    ? records.map((r, i) => ({
        value: Number(r[volumeField] ?? 0),
        itemStyle: {
          color:
            (candleData[i]?.[1] ?? 0) >= (candleData[i]?.[0] ?? 0)
              ? "rgba(34,197,94,0.5)"
              : "rgba(239,68,68,0.5)",
        },
      }))
    : [];

  const hasVolume = volumeField && volumeData.length > 0;

  const yAxisFormatter = (v: number) => formatValue(v, "compact");

  const baseOption = {
    backgroundColor: CHART_COLORS.background,
    tooltip: {
      ...createTooltip(),
      trigger: "axis" as const,
      axisPointer: { type: "cross" as const },
      formatter: (params: unknown) => {
        const arr = params as Array<{ seriesName: string; data: unknown; name: string }>;
        if (!arr?.length) return "";
        const c = arr.find((p) => p.seriesName === "Price");
        const v = arr.find((p) => p.seriesName === "Volume");
        if (!c) return "";
        const [open = 0, close = 0, low = 0, high = 0] = c.data as number[];
        const color = close >= open ? "#22c55e" : "#ef4444";
        return [
          `<span style="color:${CHART_COLORS.text.muted};font-size:10px">${c.name}</span>`,
          `<span style="color:${color}">O</span> ${formatValue(open, "compact")}`,
          `<span style="color:${color}">H</span> ${formatValue(high, "compact")}`,
          `<span style="color:${color}">L</span> ${formatValue(low, "compact")}`,
          `<span style="color:${color}">C</span> ${formatValue(close, "compact")}`,
          v ? `Vol ${formatValue(v.data as number, "compact")}` : "",
        ]
          .filter(Boolean)
          .join("<br/>");
      },
    },
    axisPointer: { link: [{ xAxisIndex: "all" }] },
    dataZoom: [
      {
        type: "inside" as const,
        xAxisIndex: hasVolume ? [0, 1] : [0],
        start: 60,
        end: 100,
      },
    ],
    grid: hasVolume
      ? [
          { ...createGrid({ bottom: "28%" }), id: "price" },
          { left: 56, right: 16, bottom: 24, top: "76%", id: "volume" },
        ]
      : createGrid(),
    xAxis: (hasVolume
      ? [
          {
            type: "category" as const,
            data: dates,
            gridIndex: 0,
            axisLabel: { show: false },
            axisLine: { lineStyle: { color: CHART_COLORS.axis.line } },
            splitLine: { show: false },
          },
          {
            type: "category" as const,
            data: dates,
            gridIndex: 1,
            axisLabel: { color: CHART_COLORS.axis.label, fontSize: 10 },
            axisLine: { lineStyle: { color: CHART_COLORS.axis.line } },
            splitLine: { show: false },
          },
        ]
      : {
          type: "category" as const,
          data: dates,
          axisLabel: { color: CHART_COLORS.axis.label, fontSize: 10 },
          axisLine: { lineStyle: { color: CHART_COLORS.axis.line } },
          splitLine: { show: false },
        }) as EChartsOption["xAxis"],
    yAxis: (hasVolume
      ? [
          {
            scale: true,
            gridIndex: 0,
            axisLabel: {
              color: CHART_COLORS.axis.label,
              fontSize: 10,
              formatter: yAxisFormatter,
            },
            axisLine: { show: false },
            splitLine: { lineStyle: { color: CHART_COLORS.axis.splitLine } },
          },
          {
            scale: true,
            gridIndex: 1,
            axisLabel: {
              color: CHART_COLORS.axis.label,
              fontSize: 10,
              formatter: yAxisFormatter,
            },
            axisLine: { show: false },
            splitLine: { show: false },
          },
        ]
      : {
          scale: true,
          axisLabel: {
            color: CHART_COLORS.axis.label,
            fontSize: 10,
            formatter: yAxisFormatter,
          },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: CHART_COLORS.axis.splitLine } },
        }) as EChartsOption["yAxis"],
    series: [
      {
        name: "Price",
        type: "candlestick" as const,
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: candleData,
        itemStyle: {
          color: CHART_COLORS.semantic.positive,
          color0: CHART_COLORS.semantic.negative,
          borderColor: CHART_COLORS.semantic.positive,
          borderColor0: CHART_COLORS.semantic.negative,
        },
      },
      ...(hasVolume
        ? [
            {
              name: "Volume",
              type: "bar" as const,
              xAxisIndex: 1,
              yAxisIndex: 1,
              data: volumeData,
            },
          ]
        : []),
    ],
  };

  return baseOption as EChartsOption;
}

const GenericCandlestickWidget: React.FC<WidgetProps<GenericCandlestickConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as GenericCandlestickConfig;

  const [showConfig, setShowConfig] = useState(!config.endpoint);
  const [draftEndpoint, setDraftEndpoint] = useState(config.endpoint ?? "");
  const [draftTitle, setDraftTitle] = useState(config.title ?? "Price");
  const [draftTs, setDraftTs] = useState(config.timestamp_field ?? "timestamp");
  const [draftOpen, setDraftOpen] = useState(config.open_field ?? "open");
  const [draftHigh, setDraftHigh] = useState(config.high_field ?? "high");
  const [draftLow, setDraftLow] = useState(config.low_field ?? "low");
  const [draftClose, setDraftClose] = useState(config.close_field ?? "close");
  const [draftVolume, setDraftVolume] = useState(config.volume_field ?? "");
  const [draftParams, setDraftParams] = useState(paramsToString(config.params));

  const containerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(300);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const h = containerRef.current.clientHeight;
        if (h > 0) setChartHeight(h);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Price");
    setDraftTs(config.timestamp_field ?? "timestamp");
    setDraftOpen(config.open_field ?? "open");
    setDraftHigh(config.high_field ?? "high");
    setDraftLow(config.low_field ?? "low");
    setDraftClose(config.close_field ?? "close");
    setDraftVolume(config.volume_field ?? "");
    setDraftParams(paramsToString(config.params));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id]);

  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: ["generic_candlestick", config.endpoint, config.params],
    queryFn: ({ signal }) => fetchEndpoint(config.endpoint, config.params ?? {}, signal),
    enabled: Boolean(config.endpoint),
    staleTime: 60_000,
    retry: 1,
  });

  const { chartOption, hasData } = useMemo(() => {
    if (!rawData) return { chartOption: {} as EChartsOption, hasData: false };
    const records = extractRecords(rawData);
    if (!records.length) return { chartOption: {} as EChartsOption, hasData: false };

    return {
      chartOption: buildCandlestickOption(
        records,
        config.timestamp_field ?? "timestamp",
        config.open_field ?? "open",
        config.high_field ?? "high",
        config.low_field ?? "low",
        config.close_field ?? "close",
        config.volume_field || undefined,
      ),
      hasData: true,
    };
  }, [rawData, config]);

  const handleSave = useCallback(() => {
    const params = parseParams(draftParams);
    onConfigChange?.({
      endpoint: draftEndpoint,
      title: draftTitle,
      timestamp_field: draftTs,
      open_field: draftOpen,
      high_field: draftHigh,
      low_field: draftLow,
      close_field: draftClose,
      ...(draftVolume ? { volume_field: draftVolume } : {}),
      ...(Object.keys(params).length ? { params } : {}),
    });
    setShowConfig(false);
  }, [draftEndpoint, draftTitle, draftTs, draftOpen, draftHigh, draftLow,
      draftClose, draftVolume, draftParams, onConfigChange]);

  const handleCancel = useCallback(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Price");
    setDraftTs(config.timestamp_field ?? "timestamp");
    setDraftOpen(config.open_field ?? "open");
    setDraftHigh(config.high_field ?? "high");
    setDraftLow(config.low_field ?? "low");
    setDraftClose(config.close_field ?? "close");
    setDraftVolume(config.volume_field ?? "");
    setDraftParams(paramsToString(config.params));
    setShowConfig(false);
  }, [config]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
          {config.title || "Candlestick"}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => refetch()} disabled={!config.endpoint}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200" title="Refresh">
            <RefreshCw size={12} />
          </button>
          <button onClick={() => setShowConfig((s) => !s)}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200" title="Configure">
            <Settings size={12} />
          </button>
        </div>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/30 bg-gray-900/50 space-y-2 overflow-y-auto max-h-64">
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Endpoint</label>
              <input type="text" value={draftEndpoint} onChange={(e) => setDraftEndpoint(e.target.value)}
                placeholder="/api/v1/ohlcv" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Title</label>
              <input type="text" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="BTC / USD" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[80px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Timestamp</label>
              <input type="text" value={draftTs} onChange={(e) => setDraftTs(e.target.value)}
                placeholder="timestamp" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Open</label>
              <input type="text" value={draftOpen} onChange={(e) => setDraftOpen(e.target.value)}
                placeholder="open" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">High</label>
              <input type="text" value={draftHigh} onChange={(e) => setDraftHigh(e.target.value)}
                placeholder="high" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Low</label>
              <input type="text" value={draftLow} onChange={(e) => setDraftLow(e.target.value)}
                placeholder="low" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Close</label>
              <input type="text" value={draftClose} onChange={(e) => setDraftClose(e.target.value)}
                placeholder="close" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
            <div className="min-w-[60px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">Volume (opt.)</label>
              <input type="text" value={draftVolume} onChange={(e) => setDraftVolume(e.target.value)}
                placeholder="volume" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">Query Params</label>
            <input type="text" value={draftParams} onChange={(e) => setDraftParams(e.target.value)}
              placeholder="symbol=BTCUSDT&interval=1d" className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={handleCancel} className="btn btn-xs btn-ghost text-slate-400">Cancel</button>
            <button onClick={handleSave} className="btn btn-xs btn-primary">Apply</button>
          </div>
        </div>
      )}

      {/* Chart */}
      <div ref={containerRef} className="flex-1 min-h-0">
        {!config.endpoint ? (
          <div className={WIDGET_EMPTY}>
            <div className="text-center space-y-2">
              <p>No endpoint configured</p>
              <button onClick={() => setShowConfig(true)} className="btn btn-xs btn-outline btn-primary">Configure</button>
            </div>
          </div>
        ) : (
          <EChartsRenderer
            chartOption={chartOption}
            height={chartHeight}
            isLoading={isLoading}
            error={error instanceof Error ? error.message : (error ? String(error) : null)}
            hasData={hasData}
            containerClassName=""
          />
        )}
      </div>
    </div>
  );
};

export default GenericCandlestickWidget;
